import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import React, { Suspense, useEffect, useState } from 'react';
import css from './GroupOwner.module.css';
import SideBarProfile from './SideBarProfile';
import AccessControlWrapper from '../../components/AccessControl/AccessControlWrapper';
import { ServiceLevel } from '../../types/business';
import AccountProfileModal from './AccountProfileModal';
import { ProfileModalProvider, useProfileModal } from './ProfileModalContext';
import { SubscriptionAdminAccountRedirect } from './SubscriptionAdminAccountRedirect';

const SideBar = React.lazy(() => import('./SideBar'));
const BulletinBoard = React.lazy(() => import('./BulletinBoard/index.tsx'));
const EmergencyCenter = React.lazy(() => import('./EmergencyCenter/index.tsx'));
const EditBulletinBoard = React.lazy(() => import('./EditBulletinBoard'));
const ViewBulletinBoard = React.lazy(() => import('./ViewBulletinBoard'));
const EmergencyBulletinBoard = React.lazy(() => import('./EmergencyBulletinBoard'));
const GroupList = React.lazy(() => import('./GroupList'));
const PaymentList = React.lazy(() => import('./Payment/PaymentList'));
const BillingPage = React.lazy(() => import('./Billing'));
const InvoiceDetail = React.lazy(() => import('./Billing/InvoiceDetail'));
const GroupDetailV2 = React.lazy(() => import('./GroupDetailV2'));
const GroupMembers = React.lazy(() => import('./GroupMembers'));
const GroupInfo = React.lazy(() => import('./GroupInfo'));
const PingList = React.lazy(() => import('./Ping/PingList'));
const ServicePlanV2 = React.lazy(() => import('./ServicePlan/ServicePlanV2'));
const NewSubscription = React.lazy(() => import('./NewSubscription'));
const PingDesign = React.lazy(() => import('./PingDesign'));
const ExtensionList = React.lazy(() => import('./ExtensionList'));
const ServiceTagList = React.lazy(() => import('./ServiceTagList'));
const ServiceInformation = React.lazy(() => import('./ServiceInformation'));
const CustomizePingPadApp = React.lazy(() => import('./CustomizePingPadApp'));
const AccessControlPolicy = React.lazy(() => import('./AccessControlPolicy'));
const GroupContentRoles = React.lazy(() => import('./GroupContentRoles'));
const CallLogs = React.lazy(() => import('./CallLogs'));
const Dashboard = React.lazy(() => import('./Dashboard'));
const BusinessDashboardPage = React.lazy(() => import('./GroupDetailV2/BusinessDashboardPage'));
const VisitorManagement = React.lazy(() => import('./VisitorManagement'));
const DoorManagement = React.lazy(() => import('./DoorManagement'));
const EntranceManagement = React.lazy(() => import('./EntranceManagement'));

export const GroupBasePath = '/group';
const OPEN_PROFILE_QUERY_KEY = 'openProfile';

const SIDEBAR_DEFAULT_WIDTH = 280;
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 500;

export const GroupOwnerSwitch = () => {
  const location = useLocation();
  const history = useHistory();
  const { openProfile } = useProfileModal();
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    if (search.get(OPEN_PROFILE_QUERY_KEY) !== '1') {
      return;
    }
    openProfile();
    search.delete(OPEN_PROFILE_QUERY_KEY);
    history.replace({
      pathname: location.pathname,
      search: search.toString() ? `?${search.toString()}` : '',
    });
  }, [location.pathname, location.search, openProfile, history]);

  return (
    <div className={css.container}>
      <SubscriptionAdminAccountRedirect />
      <Suspense fallback={<div>Loading Sidebar...</div>}>
        <Switch>
          <Route path={`${GroupBasePath}/new-subscription`}></Route>
          <Route path={`${GroupBasePath}/billing`}>
            <SideBarProfile width={sidebarWidth} onWidthChange={setSidebarWidth} minWidth={SIDEBAR_MIN_WIDTH} maxWidth={SIDEBAR_MAX_WIDTH} />
          </Route>

          <Route path={`${GroupBasePath}/:base/:groupType/:groupId`}>
            <SideBar width={sidebarWidth} onWidthChange={setSidebarWidth} minWidth={SIDEBAR_MIN_WIDTH} maxWidth={SIDEBAR_MAX_WIDTH} />
          </Route>
          <Route path={`${GroupBasePath}/`}>
            <SideBarProfile width={sidebarWidth} onWidthChange={setSidebarWidth} minWidth={SIDEBAR_MIN_WIDTH} maxWidth={SIDEBAR_MAX_WIDTH} />
          </Route>
        </Switch>
      </Suspense>

      <div className={css.body}>
        <div className={css.scrollContent}>
          <Switch>
            <Redirect exact from={`${GroupBasePath}/profile`} to={`${GroupBasePath}/dashboard?${OPEN_PROFILE_QUERY_KEY}=1`} />
            <Redirect exact from={`${GroupBasePath}/profile-management`} to={`${GroupBasePath}/dashboard?${OPEN_PROFILE_QUERY_KEY}=1`} />

            <Route exact path={`${GroupBasePath}/dashboard`}>
              <AccessControlWrapper blockAllManagement>
                <Dashboard />
              </AccessControlWrapper>
            </Route>

            {/* Subscription & Group Management Routes */}
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/dashboard`}>
              <AccessControlWrapper blockAllManagement>
                <BusinessDashboardPage />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId`}>
              <AccessControlWrapper blockAllManagement>
                <GroupDetailV2 />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/new-subscription`}>
              <AccessControlWrapper>
                <NewSubscription />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/`}>
              <AccessControlWrapper blockAllManagement>
                <Dashboard />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages`}>
              <AccessControlWrapper>
                <GroupList />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/members`}>
              <AccessControlWrapper>
                <GroupMembers />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/info`}>
              <AccessControlWrapper blockAllManagement>
                <GroupInfo />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/pings`}>
              <AccessControlWrapper>
                <PingList />
              </AccessControlWrapper>
            </Route>

            {/* Billing Routes */}
            <Route exact path={`${GroupBasePath}/billing`}>
              <AccessControlWrapper>
                <BillingPage />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/billing/invoice/:transactionNumber`}>
              <AccessControlWrapper>
                <InvoiceDetail />
              </AccessControlWrapper>
            </Route>

            {/* Payment Routes */}
            <Route exact path={`${GroupBasePath}/payments`}>
              <AccessControlWrapper>
                <PaymentList />
              </AccessControlWrapper>
            </Route>

            {/* Bulletin Board Routes */}
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/bulletin-board`}>
              <AccessControlWrapper requiredPermission="BULLETIN_BOARD" requiredServiceLevel={[ServiceLevel.Premium]}>
                <BulletinBoard />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/emergency-center`}>
              <AccessControlWrapper requiredPermission="BULLETIN_BOARD" requiredServiceLevel={[ServiceLevel.Premium]}>
                <EmergencyCenter />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/bulletin-board/new`}>
              <AccessControlWrapper requiredPermission="BULLETIN_BOARD" requiredServiceLevel={[ServiceLevel.Premium]}>
                <EditBulletinBoard />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/emergency-center/new`}>
              <AccessControlWrapper requiredPermission="BULLETIN_BOARD" requiredServiceLevel={[ServiceLevel.Premium]}>
                <EditBulletinBoard />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/bulletin-board/:id/emergency`}>
              <AccessControlWrapper requiredPermission="BULLETIN_BOARD" requiredServiceLevel={[ServiceLevel.Premium]}>
                <EmergencyBulletinBoard />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/bulletin-board/:id`}>
              <AccessControlWrapper requiredPermission="BULLETIN_BOARD" requiredServiceLevel={[ServiceLevel.Premium]}>
                <ViewBulletinBoard />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/bulletin-board/:id/edit`}>
              <AccessControlWrapper requiredPermission="BULLETIN_BOARD" requiredServiceLevel={[ServiceLevel.Premium]}>
                <EditBulletinBoard />
              </AccessControlWrapper>
            </Route>

            {/* Call Logs Routes */}
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/call-logs`}>
              <AccessControlWrapper requiredPermission="CALL_LOGS">
                <CallLogs />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/visitor-management`}>
              <AccessControlWrapper requiredPermission="ACCESS_CONTROL" requiredServiceLevel={[ServiceLevel.Standard, ServiceLevel.Premium]}>
                <VisitorManagement />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/door-management`}>
              <AccessControlWrapper requiredPermission="ACCESS_CONTROL" requiredServiceLevel={[ServiceLevel.Standard, ServiceLevel.Premium]}>
                <DoorManagement />
              </AccessControlWrapper>
            </Route>
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/entrance-management`}>
              <AccessControlWrapper requiredPermission="ACCESS_CONTROL" requiredServiceLevel={[ServiceLevel.Standard, ServiceLevel.Premium]}>
                <EntranceManagement />
              </AccessControlWrapper>
            </Route>

            {/* Extensions Routes */}
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/extensions`}>
              <AccessControlWrapper requiredPermission="EXTENSIONS">
                <ExtensionList />
              </AccessControlWrapper>
            </Route>

            {/* Services Routes */}
            <Route exact path={`${GroupBasePath}/packages/:groupType/:groupId/service-tags`}>
              <AccessControlWrapper requiredPermission="SERVICES">
                <ServiceTagList />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/service-tags/new`}>
              <AccessControlWrapper requiredPermission="SERVICES">
                <ServiceInformation />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/service-tags/:serviceId/edit`}>
              <AccessControlWrapper requiredPermission="SERVICES">
                <ServiceInformation />
              </AccessControlWrapper>
            </Route>

            {/* Business Branding Routes */}
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/design`}>
              <AccessControlWrapper requiredPermission="BUSINESS_BRANDING">
                <PingDesign />
              </AccessControlWrapper>
            </Route>

            {/* Service Plans Routes */}
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/service-plan`}>
              <AccessControlWrapper requiredPermission="SERVICE_PLANS">
                <ServicePlanV2 />
              </AccessControlWrapper>
            </Route>

            {/* Roles Routes */}
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/roles`}>
              <AccessControlWrapper requiredPermission="ROLES">
                <GroupContentRoles />
              </AccessControlWrapper>
            </Route>

            {/* App Settings Routes */}
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/customize-pingpad`}>
              <AccessControlWrapper requiredPermission="APP_SETTINGS">
                <CustomizePingPadApp />
              </AccessControlWrapper>
            </Route>
            <Route path={`${GroupBasePath}/packages/:groupType/:groupId/access-control-policy`}>
              <AccessControlWrapper requiredPermission="ACCESS_CONTROL" requiredServiceLevel={[ServiceLevel.Standard, ServiceLevel.Premium]}>
                <AccessControlPolicy />
              </AccessControlWrapper>
            </Route>
          </Switch>
        </div>
      </div>
      <AccountProfileModal />
    </div>
  );
};

const GroupOwnerSwitchWithProfileModal = () => {
  return (
    <ProfileModalProvider>
      <GroupOwnerSwitch />
    </ProfileModalProvider>
  );
};

export default GroupOwnerSwitchWithProfileModal;
